// The renderer. A read-only view of the simulation: it never writes a sim field
// and never calls world.step(). Owns the WebGL context, the lighting rig, the
// terrain mesh, the sky, the fog-of-war texture, the post-processing chain and
// the per-frame orchestration of the entity and effect layers.
//
// ============================ COLOUR PIPELINE ==============================
// EVERYTHING IN THE SCENE WRITES **LINEAR** COLOUR AND IS TONE MAPPED ONCE AT
// THE END. The scene renders into a half-float linear HDR target; OutputPass
// applies ACES filmic tone mapping and the sRGB transfer function exactly once.
// That is what makes emissive-above-1.0 and bloom possible at all, and it is
// why nothing in the scene should pre-encode sRGB values by hand.
//
// Two consequences other render modules must know about:
//
//   * A material that writes gl_FragColor itself (a raw ShaderMaterial) is NOT
//     exempt -- OutputPass tone maps whatever lands in the buffer. Authoring a
//     display-referred colour like 0x3f7fe8 and writing it straight out will
//     come back desaturated, because ACES will be applied to a value that had
//     already been encoded for display.
//   * For UI-ish colour that must survive EXACTLY as authored -- selection
//     green, the health-bar ramp, the placement ghost -- use `uiColor(hex)`
//     exported below. It runs the inverse of the tone curve, so the pixel that
//     reaches the screen is the colour you typed. Readability beats physics for
//     those, and this is how you get both.
//
// ================================ TERRAIN ==================================
// The 96x96 grid becomes ONE merged, flat-shaded BufferGeometry, not 9216
// meshes. Each tile is a quad at its own elevation; wherever two adjacent tiles
// disagree about the height of their shared edge, a vertical skirt quad is
// emitted. That single rule produces plateau walls, ramp shoulders, the rocky
// outcrops on the map's hard blockers, and the map rim, with no special cases.
// Ramp tiles are the only ones whose corners are interpolated, which is what
// turns them into visible slopes instead of steps.
//
// On top of that skeleton the builder bakes the things that make it look like
// ground rather than a chart: three octaves of value noise in the albedo, a
// second low-frequency field that mixes two ground hues so large flats are not
// one colour, a brightening gradient up every ramp with a dark rail around its
// sides and foot, rock strata and a height gradient down every cliff face,
// and -- the big one -- AMBIENT OCCLUSION baked into the vertex colours from
// the height field, so cliff bases and concave corners are dark for free,
// statically, forever. Albedo is per TILE and occlusion is per CORNER, so
// terrain-type boundaries stay crisp while the shading gradient stays smooth.
//
// Skirt triangles are individually wound to agree with their own outward normal
// (see wallTri), which is what lets the terrain be FrontSide instead of
// DoubleSide: half the rasterised fragments, and no shadow acne from back faces.
//
// ============================== LIGHTING ===================================
// One directional sun with a 2048 PCF-soft shadow map, fitted every frame to
// camera.viewRect() and snapped to whole shadow texels (see fitSunShadow);
// a hemisphere light for cool sky over warm ground bounce, which is what keeps
// shadowed faces from going to mud; and one low warm fill from the opposite
// side so silhouettes keep an edge. The sun is the ONLY shadow caster.
//
// ================================== FOG ====================================
// world.fog[player].vis is uploaded as a 96x96 single-channel texture and
// sampled by world XZ in the terrain material's fragment shader (injected via
// onBeforeCompile, AFTER distance fog so an unexplored map edge stays black
// instead of dissolving into the horizon haze): 0 unexplored -> black, 0.4
// explored -> a dark cool multiply, 1.0 visible -> untouched. It is re-uploaded
// only when fog bumps its version (12 Hz), and linear filtering softens the
// tile edges for free. Entities are culled on the CPU instead.

import * as THREE from 'three';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { OutputPass } from 'three/examples/jsm/postprocessing/OutputPass.js';
import { RTSCamera, TERRAIN_HIGH } from './camera.js';
import { EntityLayer, PALETTE, FLY_ALT } from './units.js';
import { EffectsLayer } from './effects.js';
import { BuildingDefs } from '../sim/defs.js';
import { validatePlacement } from '../sim/commands.js';

// Colour management ON: hex literals below are authored in sRGB and three
// converts them into the linear working space for us. See the header.
THREE.ColorManagement.enabled = true;

const BLOCKER_RISE = 1.15;    // hard blockers become rock outcrops
const RIM_RISE = 2.4;         // the outer two tiles become a map wall
const FLOOR_Y = -4;

export const EXPOSURE = 1.05;
const SHADOW_SIZE = 2048;
const SHADOW_PAD = 5;         // world units of casters allowed outside the view rect
const SHADOW_STEP = 4;        // quantised shadow radius, so texel size is stable
const SKY_RADIUS = 300;
const GHOST_PAD_CAP = 64;

// Bloom is deliberately timid. The threshold sits above anything the lit
// terrain or a team-coloured hull can reach, so only genuine emissive -- muzzle
// flash, engine glow, mineral crystals -- ever blooms, and the screen never
// washes out.
export const BLOOM_STRENGTH = 0.62;
export const BLOOM_RADIUS = 0.42;
export const BLOOM_THRESHOLD = 0.92;

/** Sun direction: points FROM the ground TOWARD the sun. Shadows fall +x/+z,
 *  i.e. down and to the right on screen, which is where an RTS player reads
 *  them. Kept as a unit vector; the light rig and the shadow fit both use it. */
const SUN_DIR = new THREE.Vector3(-0.48, 0.82, -0.31).normalize();

// ---------------------------------------------------------------------------
// ACES tone curve, in JS.
//
// three applies this in a shader at the very end of the frame. Reproducing it
// here lets us do the one thing a physically-correct pipeline otherwise makes
// impossible: author a colour that comes out of the far end EXACTLY as typed.
// Every readability-critical overlay in the game depends on that.
// ---------------------------------------------------------------------------
const ACES_IN = [
  [0.59719, 0.35458, 0.04823],
  [0.07600, 0.90834, 0.01566],
  [0.02840, 0.13383, 0.83777],
];
const ACES_OUT = [
  [1.60475, -0.53108, -0.07367],
  [-0.10208, 1.10813, -0.00605],
  [-0.00327, -0.07276, 1.07602],
];

/**
 * The two ACES matrices are NOT each other's inverse -- they differ by about
 * 8%, which is more than enough to turn "exactly the colour you typed" into
 * "close enough that nobody notices until they do". Invert them properly.
 */
function invert3(m) {
  const [a, b, c] = m[0], [d, e, f] = m[1], [g, h, i] = m[2];
  const A = e * i - f * h, B = -(d * i - f * g), C = d * h - e * g;
  const det = a * A + b * B + c * C;
  return [
    [A / det, -(b * i - c * h) / det, (b * f - c * e) / det],
    [B / det, (a * i - c * g) / det, -(a * f - c * d) / det],
    [C / det, -(a * h - b * g) / det, (a * e - b * d) / det],
  ];
}

const mul3 = (m, v, out) => {
  const x = v[0], y = v[1], z = v[2];
  out[0] = m[0][0] * x + m[0][1] * y + m[0][2] * z;
  out[1] = m[1][0] * x + m[1][1] * y + m[1][2] * z;
  out[2] = m[2][0] * x + m[2][1] * y + m[2][2] * z;
  return out;
};
const rrtFit = (v) => (v * (v + 0.0245786) - 0.000090537) / (v * (0.983729 * v + 0.432951) + 0.238081);
/** Inverse of RRTAndODTFit: solve the quadratic it defines for v given y. */
const rrtFitInv = (y) => {
  const A = 1 - 0.983729 * y;
  const B = 0.0245786 - 0.432951 * y;
  const C = -0.000090537 - 0.238081 * y;
  if (Math.abs(A) < 1e-9) return -C / B;
  const disc = B * B - 4 * A * C;
  if (disc <= 0) return 0;
  const v = (-B + Math.sqrt(disc)) / (2 * A);
  return Number.isFinite(v) ? v : 0;
};

const ACES_IN_INV = invert3(ACES_IN);
const ACES_OUT_INV = invert3(ACES_OUT);

const _a3 = [0, 0, 0], _b3 = [0, 0, 0];

/** three's ACESFilmicToneMapping, on a linear RGB triple. */
export function acesToneMap(rgb, exposure = EXPOSURE) {
  _a3[0] = rgb[0] * exposure; _a3[1] = rgb[1] * exposure; _a3[2] = rgb[2] * exposure;
  mul3(ACES_IN, _a3, _b3);
  _b3[0] = rrtFit(_b3[0]); _b3[1] = rrtFit(_b3[1]); _b3[2] = rrtFit(_b3[2]);
  mul3(ACES_OUT, _b3, _a3);
  return [
    Math.min(1, Math.max(0, _a3[0])),
    Math.min(1, Math.max(0, _a3[1])),
    Math.min(1, Math.max(0, _a3[2])),
  ];
}

/**
 * Inverse of acesToneMap: the scene value that tone maps to `rgb`.
 *
 * Two things fall out of this that callers need to know. A saturated colour
 * near the edge of the ACES gamut inverts to a NEGATIVE value in one channel --
 * that is not a bug, it is the only way to hit that colour, the half-float
 * render target carries it fine and the tone mapper's final saturate() cleans
 * it up. And a bright saturated colour inverts to well ABOVE 1.0, which means
 * it will cross the bloom threshold and glow. For a selection ring or a
 * placement ghost that is a feature; for anything that must stay matte, scale
 * the result down.
 */
export function acesToneMapInverse(rgb, exposure = EXPOSURE) {
  _a3[0] = Math.min(0.999, Math.max(0, rgb[0]));
  _a3[1] = Math.min(0.999, Math.max(0, rgb[1]));
  _a3[2] = Math.min(0.999, Math.max(0, rgb[2]));
  mul3(ACES_OUT_INV, _a3, _b3);
  _b3[0] = rrtFitInv(_b3[0]); _b3[1] = rrtFitInv(_b3[1]); _b3[2] = rrtFitInv(_b3[2]);
  mul3(ACES_IN_INV, _b3, _a3);
  const k = 1 / exposure;
  return [_a3[0] * k, _a3[1] * k, _a3[2] * k];
}

const _col = new THREE.Color();
/** Linear working-space triple for an sRGB hex. */
export function linear(hex) {
  _col.setHex(hex, THREE.SRGBColorSpace);
  return [_col.r, _col.g, _col.b];
}

/**
 * The scene-space colour that will land on screen as EXACTLY the sRGB hex you
 * typed, after ACES and the sRGB transfer function. Use this for every colour
 * whose job is to be recognised rather than to be lit: selection green, the
 * health ramp, the placement ghost, the sky gradient, the fog colour.
 */
export function uiColor(hex) { return acesToneMapInverse(linear(hex)); }

// ---------------------------------------------------------------------------
// Terrain palette. Authored in sRGB; converted lazily so the module still
// imports cleanly if three's colour management flips.
// ---------------------------------------------------------------------------
// Tuned against the actual output of the pipeline, not by eye: every value
// here was pushed through the lighting rig, ACES and the sRGB transfer curve
// and checked as a final screen luminance. The targets that matter, all at
// gameplay zoom on lit ground:
//
//   low ground 0.40 | in shadow 0.20   -- a readable 2:1, never crushed
//   high ground 0.57                   -- unmistakably lighter than low
//   ramp 0.62 with 0.28 rails          -- the single most legible thing on the map
//   cliff band 0.24, cliff face 0.41 at the lip falling to 0.09 at the foot
//
// Authoring these as "nice hex colours" and hoping is how terrain ends up as a
// dark green diagram: ACES compresses midtones hard, and a palette that looks
// right in a swatch renders two stops too dark.
const TERRAIN_PALETTE = {
  lowA: 0x6d7a58,      // walkable low ground, damp olive
  lowB: 0x83815f,      // walkable low ground, drier patch hue
  highA: 0xa89a76,     // walkable high ground, warm tan -- lighter than low
  highB: 0xb5a481,
  ramp: 0xc2ac74,      // ramps, warmer and brighter so the slope reads
  rampRail: 0x6d5f42,  // the ramp's lateral edge where it meets the cliff
  cliff: 0x585448,     // the unwalkable band at the foot of a cliff
  blocker: 0x6b6454,   // unwalkable rock outcrop
  wall: 0x7d7468,      // vertical skirt faces (the actual cliff rock)
};

const ROUGH = {
  low: 0.94, high: 0.88, ramp: 0.79, cliff: 0.97, blocker: 0.98,
};

// ---------------------------------------------------------------------------
// Deterministic value noise. No Math.random anywhere in the renderer.
// ---------------------------------------------------------------------------
function hash2(x, y) {
  let h = (x * 374761393 + y * 668265263) | 0;
  h = (h ^ (h >>> 13)) * 1274126177 | 0;
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}

/** Per-tile jitter, kept from the original renderer for fine-grain break-up. */
export function tileNoise(x, y) { return hash2(x, y); }

function vnoise(x, y) {
  const xi = Math.floor(x), yi = Math.floor(y);
  const fx = x - xi, fy = y - yi;
  const ux = fx * fx * (3 - 2 * fx);
  const uy = fy * fy * (3 - 2 * fy);
  const a = hash2(xi, yi), b = hash2(xi + 1, yi);
  const c = hash2(xi, yi + 1), d = hash2(xi + 1, yi + 1);
  return (a * (1 - ux) + b * ux) * (1 - uy) + (c * (1 - ux) + d * ux) * uy;
}
function fbm(x, y) {
  return 0.56 * vnoise(x, y)
    + 0.30 * vnoise(x * 2.13 + 11.3, y * 2.13 - 7.1)
    + 0.14 * vnoise(x * 4.71 - 3.2, y * 4.71 + 5.9);
}

/**
 * Build the terrain mesh plus a height sampler that reports exactly the surface
 * that was generated (so click-picking lands where the player sees ground).
 *
 * Returns { geometry, sampleHeight, tileZ, vertexCount, minZ, maxZ, surfaceTex }.
 */
export function buildTerrain(grid) {
  const w = grid.w, h = grid.h;
  const n = w * h;

  // --- 1. per-tile elevation, with ramps interpolated between the levels ----
  const tileZ = new Float32Array(n);
  const isRamp = new Uint8Array(n);

  const dHigh = new Int32Array(n).fill(-1);
  const dLow = new Int32Array(n).fill(-1);
  const queue = new Int32Array(n);

  const bfs = (dist, wantHigh) => {
    let head = 0, tail = 0;
    for (let i = 0; i < n; i++) {
      if (grid.ramp[i] || grid.cliff[i]) continue;
      const hi = grid.height[i] === 1;
      if (hi === wantHigh) { dist[i] = 0; queue[tail++] = i; }
    }
    while (head < tail) {
      const i = queue[head++];
      const x = i % w, y = (i / w) | 0;
      const d = dist[i] + 1;
      for (let k = 0; k < 4; k++) {
        const nx = x + (k === 0 ? 1 : k === 1 ? -1 : 0);
        const ny = y + (k === 2 ? 1 : k === 3 ? -1 : 0);
        if (nx < 0 || ny < 0 || nx >= w || ny >= h) continue;
        const j = ny * w + nx;
        if (!grid.ramp[j]) continue;          // only propagate through ramps
        if (dist[j] !== -1) continue;
        dist[j] = d;
        queue[tail++] = j;
      }
    }
  };
  bfs(dHigh, true);
  bfs(dLow, false);

  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const i = y * w + x;
      let z;
      if (grid.ramp[i]) {
        isRamp[i] = 1;
        const a = dLow[i], b = dHigh[i];
        let t;
        if (a < 0) t = 1;
        else if (b < 0) t = 0;
        else {
          const den = a + b - 2;
          t = den <= 0 ? 0.5 : (a - 1) / den;
        }
        z = Math.max(0, Math.min(1, t)) * TERRAIN_HIGH;
      } else {
        z = grid.height[i] === 1 ? TERRAIN_HIGH : 0;
      }
      if (grid.cliff[i] === 2) z += BLOCKER_RISE;
      if (x < 2 || y < 2 || x >= w - 2 || y >= h - 2) z += RIM_RISE;
      tileZ[i] = z;
    }
  }

  let minZ = Infinity, maxZ = -Infinity;
  for (let i = 0; i < n; i++) { if (tileZ[i] < minZ) minZ = tileZ[i]; if (tileZ[i] > maxZ) maxZ = tileZ[i]; }

  // --- 2. corner field, used to slope ramp quads and to anchor the AO bake --
  const cw = w + 1;
  const cornerZ = new Float32Array(cw * (h + 1));
  for (let j = 0; j <= h; j++) {
    for (let i = 0; i <= w; i++) {
      let s = 0, c = 0;
      for (let dy = -1; dy <= 0; dy++) {
        for (let dx = -1; dx <= 0; dx++) {
          const tx = i + dx, ty = j + dy;
          if (tx < 0 || ty < 0 || tx >= w || ty >= h) continue;
          s += tileZ[ty * w + tx]; c++;
        }
      }
      cornerZ[j * cw + i] = c ? s / c : 0;
    }
  }

  /** Elevation of the rendered surface at fractional tile coords. */
  const sampleHeight = (fx, fy) => {
    let xi = Math.floor(fx), yi = Math.floor(fy);
    if (xi < 0) xi = 0; else if (xi > w - 1) xi = w - 1;
    if (yi < 0) yi = 0; else if (yi > h - 1) yi = h - 1;
    const i = yi * w + xi;
    if (!isRamp[i]) return tileZ[i];
    const u = Math.min(1, Math.max(0, fx - xi));
    const v = Math.min(1, Math.max(0, fy - yi));
    const z00 = cornerZ[yi * cw + xi];
    const z10 = cornerZ[yi * cw + xi + 1];
    const z01 = cornerZ[(yi + 1) * cw + xi];
    const z11 = cornerZ[(yi + 1) * cw + xi + 1];
    return (z00 * (1 - u) + z10 * u) * (1 - v) + (z01 * (1 - u) + z11 * u) * v;
  };

  // --- 3. baked ambient occlusion on the corner lattice ---------------------
  // For every mesh corner, ask how much of the surrounding height field stands
  // ABOVE it, distance-weighted. Cliff bases, the insides of the map rim and
  // every concave corner come out dark; open flats come out at 1.0. This is the
  // cheapest large perceived-quality win available on static terrain: it costs
  // one pass at load and nothing per frame.
  const AO_R = 3;
  const AO_CAP = 2.2;               // a drop deeper than this occludes no further
  const cornerAO = new Float32Array(cw * (h + 1));
  {
    // Precompute the distance weights once instead of a hypot per sample.
    const span = AO_R * 2;
    const wgt = new Float32Array(span * span);
    for (let dy = 0; dy < span; dy++) {
      for (let dx = 0; dx < span; dx++) {
        const ox = dx - AO_R + 0.5, oy = dy - AO_R + 0.5;
        wgt[dy * span + dx] = 1 / (1 + Math.hypot(ox, oy));
      }
    }
    let wsum = 0;
    for (let i = 0; i < wgt.length; i++) wsum += wgt[i];

    for (let j = 0; j <= h; j++) {
      for (let i = 0; i <= w; i++) {
        const zc = cornerZ[j * cw + i];
        let occ = 0;
        for (let dy = 0; dy < span; dy++) {
          let ty = j + dy - AO_R;
          if (ty < 0) ty = 0; else if (ty > h - 1) ty = h - 1;
          const row = ty * w;
          const wrow = dy * span;
          for (let dx = 0; dx < span; dx++) {
            let tx = i + dx - AO_R;
            if (tx < 0) tx = 0; else if (tx > w - 1) tx = w - 1;
            const d = tileZ[row + tx] - zc;
            if (d > 0) occ += wgt[wrow + dx] * (d > AO_CAP ? AO_CAP : d);
          }
        }
        const k = occ / (wsum * AO_CAP);
        cornerAO[j * cw + i] = Math.max(0.55, 1 - 0.85 * k);
      }
    }
  }

  // --- 4. per-tile albedo class + roughness field --------------------------
  const P = {};
  for (const k in TERRAIN_PALETTE) P[k] = linear(TERRAIN_PALETTE[k]);

  const tileR = new Float32Array(n), tileG = new Float32Array(n), tileB = new Float32Array(n);
  const surf = new Uint8Array(n * 4);      // R unused, G roughness, B metalness

  // "Rails": the sides and foot of a ramp, darkened so the slope reads as a
  // cut inset into the ground instead of a bright rectangle painted on top.
  //
  // The rule is deliberately geometric rather than clever. A ramp tile is a
  // rail if it touches a non-ramp tile that is NOT high ground -- which traces
  // the ramp's two long sides and its foot, and stops at the top so the ramp
  // merges into the plateau it serves. Anything derived from the ramp BFS
  // distances instead is wrong: dLow floods in from the low ground beside a
  // ramp as well as from its foot, so the "steps from the bottom" you would be
  // reading is 1 all along both edges.
  const isRail = new Uint8Array(n);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const i = y * w + x;
      if (!isRamp[i]) continue;
      for (let k = 0; k < 4; k++) {
        const nx = x + (k === 0 ? 1 : k === 1 ? -1 : 0);
        const ny = y + (k === 2 ? 1 : k === 3 ? -1 : 0);
        if (nx < 0 || ny < 0 || nx >= w || ny >= h) continue;
        const j = ny * w + nx;
        if (isRamp[j]) continue;
        if (grid.height[j] === 1 && !grid.cliff[j]) continue;   // the top: merge in
        isRail[i] = 1; break;
      }
    }
  }

  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const i = y * w + x;
      // low-frequency hue field: two ground hues mixed in big soft patches
      const patch = vnoise(x * 0.043 + 5.5, y * 0.043 - 2.25);
      // high-frequency value break-up
      const grain = 0.88 + 0.26 * fbm(x * 0.135, y * 0.135);

      let a, b, mix = patch, k = grain, rough;
      if (grid.cliff[i] === 2) { a = P.blocker; b = P.blocker; rough = ROUGH.blocker; k = 0.82 + 0.34 * grain; }
      else if (grid.cliff[i]) { a = P.cliff; b = P.cliff; rough = ROUGH.cliff; k = 0.84 + 0.30 * grain; }
      else if (isRamp[i]) {
        a = isRail[i] ? P.rampRail : P.ramp;
        b = a;
        rough = ROUGH.ramp;
        // The ramp brightens as it climbs. Alternating tread bands would read
        // better, but a ramp on this map is three tiles of slope and the mesh
        // is flat-coloured per tile, so any banding period either aliases into
        // a constant or into noise. A monotone gradient always reads, and it
        // reinforces the thing the player is actually being told: this goes up.
        k = grain * (0.88 + 0.26 * (tileZ[i] / TERRAIN_HIGH));
      } else if (grid.height[i] === 1) { a = P.highA; b = P.highB; rough = ROUGH.high; }
      else { a = P.lowA; b = P.lowB; rough = ROUGH.low; }

      tileR[i] = (a[0] * (1 - mix) + b[0] * mix) * k;
      tileG[i] = (a[1] * (1 - mix) + b[1] * mix) * k;
      tileB[i] = (a[2] * (1 - mix) + b[2] * mix) * k;

      const r = Math.max(0.05, Math.min(1, rough + (grain - 1) * 0.55));
      surf[i * 4] = 255;
      surf[i * 4 + 1] = (r * 255) | 0;
      surf[i * 4 + 2] = 0;
      surf[i * 4 + 3] = 255;
    }
  }

  // --- 5. emit geometry ----------------------------------------------------
  const maxVerts = n * 6 + 64000;
  const pos = new Float32Array(maxVerts * 3);
  const nor = new Float32Array(maxVerts * 3);
  const col = new Float32Array(maxVerts * 3);
  const uvs = new Float32Array(maxVerts * 2);
  let vc = 0;

  const iw = 1 / w, ih = 1 / h;
  const put = (x, y, z, nx, ny, nz, r, g, b) => {
    const o = vc * 3;
    pos[o] = x; pos[o + 1] = y; pos[o + 2] = z;
    nor[o] = nx; nor[o + 1] = ny; nor[o + 2] = nz;
    col[o] = r; col[o + 1] = g; col[o + 2] = b;
    uvs[vc * 2] = x * iw; uvs[vc * 2 + 1] = z * ih;
    vc++;
  };

  const cz = (i, j) => cornerZ[j * cw + i];
  const ao = (i, j) => cornerAO[j * cw + i];
  const z00 = new Float32Array(n), z10 = new Float32Array(n);
  const z11 = new Float32Array(n), z01 = new Float32Array(n);

  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const i = y * w + x;
      if (isRamp[i]) {
        z00[i] = cz(x, y); z10[i] = cz(x + 1, y);
        z11[i] = cz(x + 1, y + 1); z01[i] = cz(x, y + 1);
      } else {
        const z = tileZ[i];
        z00[i] = z; z10[i] = z; z11[i] = z; z01[i] = z;
      }
    }
  }

  const tmpN = [0, 0, 0];
  const faceNormal = (ax, ay, az, bx, by, bz, cx2, cy2, cz2) => {
    const e1x = bx - ax, e1y = by - ay, e1z = bz - az;
    const e2x = cx2 - ax, e2y = cy2 - ay, e2z = cz2 - az;
    const nx = e1y * e2z - e1z * e2y;
    const ny = e1z * e2x - e1x * e2z;
    const nz = e1x * e2y - e1y * e2x;
    const len = Math.hypot(nx, ny, nz) || 1;
    tmpN[0] = nx / len; tmpN[1] = ny / len; tmpN[2] = nz / len;
    return tmpN;
  };

  // Ground quads. Colour is per-CORNER (AO) over a per-TILE albedo, so the
  // occlusion gradient is smooth while terrain-type boundaries stay crisp --
  // which is the readability half of the deal.
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const i = y * w + x;
      const r = tileR[i], g = tileG[i], b = tileB[i];
      const k00 = ao(x, y), k10 = ao(x + 1, y), k11 = ao(x + 1, y + 1), k01 = ao(x, y + 1);
      const a00 = z00[i], a10 = z10[i], a11 = z11[i], a01 = z01[i];

      let nrm = faceNormal(x, a00, y, x, a01, y + 1, x + 1, a11, y + 1);
      put(x, a00, y, nrm[0], nrm[1], nrm[2], r * k00, g * k00, b * k00);
      put(x, a01, y + 1, nrm[0], nrm[1], nrm[2], r * k01, g * k01, b * k01);
      put(x + 1, a11, y + 1, nrm[0], nrm[1], nrm[2], r * k11, g * k11, b * k11);
      nrm = faceNormal(x, a00, y, x + 1, a11, y + 1, x + 1, a10, y);
      put(x, a00, y, nrm[0], nrm[1], nrm[2], r * k00, g * k00, b * k00);
      put(x + 1, a11, y + 1, nrm[0], nrm[1], nrm[2], r * k11, g * k11, b * k11);
      put(x + 1, a10, y, nrm[0], nrm[1], nrm[2], r * k10, g * k10, b * k10);
    }
  }

  // --- 6. vertical skirts --------------------------------------------------
  // Wound to agree with the outward normal we hand in, so the whole terrain can
  // be FrontSide. Colour runs dark at the foot of the face to bright at its
  // lip: a free contact shadow plus the highlight that makes high ground read
  // as high, with rock strata from the noise field on top.
  const EPS = 1e-4;
  const wallCol = P.wall;
  const qx = [0, 0, 0, 0], qy = [0, 0, 0, 0], qz = [0, 0, 0, 0];

  const wallVertex = (idx, ymin, ymax) => {
    const py = qy[idx];
    const t = ymax - ymin > 1e-4 ? (py - ymin) / (ymax - ymin) : 1;
    const shade = 0.66 + 0.42 * Math.pow(t, 0.70);
    const strata = 0.78 + 0.46 * fbm(qx[idx] * 0.55 + py * 1.7, qz[idx] * 0.55 - py * 1.1);
    const k = shade * strata;
    put(qx[idx], py, qz[idx], 0, 0, 0, wallCol[0] * k, wallCol[1] * k, wallCol[2] * k);
  };

  /**
   * One skirt triangle, wound to face `n`.
   *
   * The check is PER TRIANGLE, not per quad, and that is not paranoia: where a
   * ramp shoulder meets a plateau the two tiles sharing an edge can be higher
   * at one end and lower at the other, so the "quad" self-intersects and its
   * two halves genuinely face opposite ways. Deciding the winding once for the
   * pair leaves one triangle inside-out -- invisible under FrontSide, which
   * shows up as a hole in the cliff exactly at the top of every ramp.
   */
  const wallTri = (a, b, c, nx, nz, ymin, ymax) => {
    const e1x = qx[b] - qx[a], e1y = qy[b] - qy[a], e1z = qz[b] - qz[a];
    const e2x = qx[c] - qx[a], e2y = qy[c] - qy[a], e2z = qz[c] - qz[a];
    const gx = e1y * e2z - e1z * e2y;
    const gz = e1x * e2y - e1y * e2x;
    if (gx * nx + gz * nz < 0) { const t = b; b = c; c = t; }
    const base = vc;
    wallVertex(a, ymin, ymax); wallVertex(b, ymin, ymax); wallVertex(c, ymin, ymax);
    for (let i = base; i < vc; i++) { nor[i * 3] = nx; nor[i * 3 + 1] = 0; nor[i * 3 + 2] = nz; }
  };

  const wallQuad = (p0x, p0y, p0z, p1x, p1y, p1z, p2x, p2y, p2z, p3x, p3y, p3z, nx, nz) => {
    qx[0] = p0x; qy[0] = p0y; qz[0] = p0z;
    qx[1] = p1x; qy[1] = p1y; qz[1] = p1z;
    qx[2] = p2x; qy[2] = p2y; qz[2] = p2z;
    qx[3] = p3x; qy[3] = p3y; qz[3] = p3z;

    let ymin = qy[0], ymax = qy[0];
    for (let i = 1; i < 4; i++) { if (qy[i] < ymin) ymin = qy[i]; if (qy[i] > ymax) ymax = qy[i]; }

    wallTri(0, 1, 2, nx, nz, ymin, ymax);
    wallTri(0, 2, 3, nx, nz, ymin, ymax);
  };

  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const i = y * w + x;
      // east edge
      if (x + 1 < w) {
        const j = i + 1;
        if (Math.abs(z10[i] - z00[j]) > EPS || Math.abs(z11[i] - z01[j]) > EPS) {
          const aHigher = z10[i] + z11[i] > z00[j] + z01[j];
          wallQuad(
            x + 1, z10[i], y, x + 1, z11[i], y + 1,
            x + 1, z01[j], y + 1, x + 1, z00[j], y,
            aHigher ? 1 : -1, 0,
          );
        }
      } else {
        wallQuad(w, z10[i], y, w, z11[i], y + 1, w, FLOOR_Y, y + 1, w, FLOOR_Y, y, 1, 0);
      }
      // south edge
      if (y + 1 < h) {
        const j = i + w;
        if (Math.abs(z01[i] - z00[j]) > EPS || Math.abs(z11[i] - z10[j]) > EPS) {
          const aHigher = z01[i] + z11[i] > z00[j] + z10[j];
          wallQuad(
            x, z01[i], y + 1, x + 1, z11[i], y + 1,
            x + 1, z10[j], y + 1, x, z00[j], y + 1,
            0, aHigher ? 1 : -1,
          );
        }
      } else {
        wallQuad(x, z01[i], h, x + 1, z11[i], h, x + 1, FLOOR_Y, h, x, FLOOR_Y, h, 0, 1);
      }
      if (x === 0) wallQuad(0, z00[i], y, 0, FLOOR_Y, y, 0, FLOOR_Y, y + 1, 0, z01[i], y + 1, -1, 0);
      if (y === 0) wallQuad(x, z00[i], 0, x, FLOOR_Y, 0, x + 1, FLOOR_Y, 0, x + 1, z10[i], 0, 0, -1);
    }
  }

  const geo = new THREE.BufferGeometry();
  const colAttr = new THREE.BufferAttribute(col.subarray(0, vc * 3), 3);
  geo.setAttribute('position', new THREE.BufferAttribute(pos.subarray(0, vc * 3), 3));
  geo.setAttribute('normal', new THREE.BufferAttribute(nor.subarray(0, vc * 3), 3));
  geo.setAttribute('color', colAttr);
  geo.setAttribute('uv', new THREE.BufferAttribute(uvs.subarray(0, vc * 2), 2));
  // Same BufferAttribute object under the old name: nothing that referenced
  // `aTint` breaks, and it costs no extra buffer upload.
  geo.setAttribute('aTint', colAttr);
  geo.computeBoundingSphere();

  const surfaceTex = new THREE.DataTexture(surf, w, h, THREE.RGBAFormat, THREE.UnsignedByteType);
  surfaceTex.minFilter = THREE.LinearFilter;
  surfaceTex.magFilter = THREE.LinearFilter;
  surfaceTex.wrapS = THREE.ClampToEdgeWrapping;
  surfaceTex.wrapT = THREE.ClampToEdgeWrapping;
  surfaceTex.generateMipmaps = false;
  surfaceTex.flipY = false;
  surfaceTex.needsUpdate = true;

  return { geometry: geo, sampleHeight, tileZ, vertexCount: vc, minZ, maxZ, surfaceTex, cornerAO };
}

// Fog-of-war levels written into the texture: unexplored, explored, visible.
// Exported because these three numbers ARE the gameplay contract -- unexplored
// must be exactly 0 so it multiplies to black, and the gap between explored and
// visible has to survive tone mapping, which compresses midtones.
export const FOG_LUT = [0.0, 0.4, 1.0];

// ---------------------------------------------------------------------------
// scratch, module level -- nothing in the frame path allocates
// ---------------------------------------------------------------------------
const _sunRight = new THREE.Vector3();
const _sunUp = new THREE.Vector3();
const _sunCentre = new THREE.Vector3();
const _worldUp = new THREE.Vector3(0, 1, 0);
const _rectScratch = { x0: 0, y0: 0, x1: 0, y1: 0 };
const _size = new THREE.Vector2();

export class Renderer {
  constructor(canvas, world, player = 0) {
    this.canvas = canvas;
    this.world = world;
    this.player = player;

    this.three = new THREE.WebGLRenderer({
      canvas, antialias: true, alpha: false, powerPreference: 'high-performance', stencil: false,
    });
    this.three.setClearColor(0x0a0d14, 1);
    this.three.autoClear = true;
    this.three.outputColorSpace = THREE.SRGBColorSpace;
    this.three.toneMapping = THREE.ACESFilmicToneMapping;
    this.three.toneMappingExposure = EXPOSURE;
    this.three.shadowMap.enabled = true;
    this.three.shadowMap.type = THREE.PCFSoftShadowMap;
    // Draw calls are read after the whole composer chain, so three must not
    // reset the counters between passes.
    this.three.info.autoReset = false;

    this.scene = new THREE.Scene();

    // --- terrain ----------------------------------------------------------
    const t = buildTerrain(world.grid);
    this.sampleHeight = t.sampleHeight;
    this.terrainVerts = t.vertexCount;
    this.terrainMinZ = t.minZ;
    this.terrainMaxZ = t.maxZ;
    this.surfaceTex = t.surfaceTex;

    const g = world.grid;
    this._fogData = new Uint8Array(g.w * g.h);
    this.fogTex = new THREE.DataTexture(this._fogData, g.w, g.h, THREE.RedFormat, THREE.UnsignedByteType);
    this.fogTex.minFilter = THREE.LinearFilter;
    this.fogTex.magFilter = THREE.LinearFilter;
    this.fogTex.wrapS = THREE.ClampToEdgeWrapping;
    this.fogTex.wrapT = THREE.ClampToEdgeWrapping;
    this.fogTex.generateMipmaps = false;
    this.fogTex.flipY = false;
    this.fogTex.unpackAlignment = 1;
    this.fogTex.needsUpdate = true;
    this._fogVersion = -1;

    this.terrainMat = makeTerrainMaterial(this.fogTex, this.surfaceTex);
    this.terrain = new THREE.Mesh(t.geometry, this.terrainMat);
    this.terrain.frustumCulled = false;
    this.terrain.renderOrder = 0;
    this.terrain.matrixAutoUpdate = false;
    this.terrain.updateMatrix();
    this.terrain.castShadow = true;      // cliffs shadowing their own bases
    this.terrain.receiveShadow = true;
    this.scene.add(this.terrain);

    // --- sky + atmosphere -------------------------------------------------
    this._buildSky();
    this.scene.fog = new THREE.Fog(0x000000, 60, 140);
    this.scene.fog.color.setRGB(HORIZON[0], HORIZON[1], HORIZON[2], THREE.LinearSRGBColorSpace);

    // --- lighting rig -----------------------------------------------------
    this._buildLights();

    // --- camera -----------------------------------------------------------
    this.camera = new RTSCamera(world);
    this.camera.setHeightSampler(this.sampleHeight);
    // viewRect has to bracket screenToWorld at EVERY pixel, and screenToWorld
    // picks the real surface -- which runs from the map floor to the top of the
    // rim wall, not just 0..TERRAIN_HIGH.
    this.camera.setHeightRange(t.minZ, t.maxZ);
    this.camera.attach(canvas);

    // --- layers -----------------------------------------------------------
    this.entities = new EntityLayer(this.scene, world, player);
    this.entities.setHeightSampler(this.sampleHeight);
    this.fx = new EffectsLayer(this.scene, world, this.sampleHeight);
    this._shadowTagged = -1;
    this._shadowTagCountdown = 0;
    this._tagOne = (o) => {
      if (!o.isMesh && !o.isInstancedMesh) return;
      if (o === this.sky || o === this.terrain) return;
      const m = o.material;
      if (!m || Array.isArray(m)) return;
      const solid = !m.transparent && m.depthWrite !== false;
      o.castShadow = solid;
      o.receiveShadow = solid;
    };
    this._tagShadowCasters();

    this.selection = new Set();
    this.placement = null;
    this._buildPlacementGhost();

    // EVENT DRAIN CONTRACT. world.drainEvents() is destructive, so exactly one
    // module in the frame may call it -- and main.js runs render() before
    // hud.update(), which makes the renderer that module whether it wants to be
    // or not. It drains once per frame and republishes the batch:
    //
    //   renderer.getFrameEvents()   the array drained this frame
    //   renderer.onEvents(cb)       subscribe; returns an unsubscribe function
    //
    // The HUD reaches the renderer through input.renderer. Nothing else may
    // call world.drainEvents(): a second caller silently gets an empty array
    // and its alerts/sounds/kill feed just never fire.
    this.frameEvents = [];
    this._eventCbs = [];

    this._camUp = new THREE.Vector3(0, 1, 0);
    this._last = (typeof performance !== 'undefined' ? performance.now() : Date.now());
    this.stats = {
      frameMs: 0, cpuMs: 0, drawCalls: 0, triangles: 0,
      units: 0, buildings: 0, particles: 0, fps: 0, postfx: false,
    };
    this._fpsAcc = 0; this._fpsN = 0; this._msAcc = 0;

    // --- post-processing + overlays --------------------------------------
    // resize() first: the composer's render target and its MSAA sample count
    // are chosen from the final pixel ratio, and rebuilding them is not free.
    this.postEnabled = true;
    this.composer = null;
    this.bloom = null;
    this._composerSamples = -1;
    this.resize();
    this._buildComposer();
    this._buildOverlay();
    this._fitShadow();
  }

  // ---- setup helpers -----------------------------------------------------
  _buildSky() {
    const geo = new THREE.SphereGeometry(SKY_RADIUS, 24, 16);
    const p = geo.attributes.position;
    const c = new Float32Array(p.count * 3);
    for (let i = 0; i < p.count; i++) {
      const t = p.getY(i) / SKY_RADIUS;          // -1 (nadir) .. 1 (zenith)
      const s = skyGradient(t);
      c[i * 3] = s[0]; c[i * 3 + 1] = s[1]; c[i * 3 + 2] = s[2];
    }
    geo.setAttribute('color', new THREE.BufferAttribute(c, 3));
    const mat = new THREE.MeshBasicMaterial({
      vertexColors: true, side: THREE.BackSide, depthWrite: false, depthTest: false, fog: false,
    });
    this.sky = new THREE.Mesh(geo, mat);
    this.sky.frustumCulled = false;
    this.sky.matrixAutoUpdate = false;
    this.sky.renderOrder = -1000;
    this.scene.add(this.sky);
  }

  _buildLights() {
    // ONE shadow-casting light. Everything else is fill.
    const sun = new THREE.DirectionalLight(0xfff2d8, 2.8);
    sun.castShadow = true;
    sun.shadow.mapSize.set(SHADOW_SIZE, SHADOW_SIZE);
    sun.shadow.bias = -0.0004;
    sun.shadow.normalBias = 0.05;
    sun.shadow.camera.near = 0.5;
    sun.shadow.camera.far = 260;
    sun.position.copy(SUN_DIR).multiplyScalar(80);
    sun.target.position.set(0, 0, 0);
    this.scene.add(sun);
    this.scene.add(sun.target);
    this.sun = sun;

    // Cool sky bounce over warm dust: this is what stops shadowed faces from
    // going to mud, and it is most of why the scene reads as outdoors.
    this.hemi = new THREE.HemisphereLight(0xa9c9ee, 0x96866a, 2.4);
    this.scene.add(this.hemi);

    // Low warm fill from the opposite side, no shadow, so silhouettes keep an
    // edge against dark terrain.
    this.fill = new THREE.DirectionalLight(0xffcf9a, 0.60);
    this.fill.position.set(0.62, 0.35, 0.70).multiplyScalar(60);
    this.scene.add(this.fill);
  }

  /**
   * Turn shadow casting on for the solid geometry the entity and effect layers
   * put in the scene, without reaching into their source. The rule: a material
   * that is opaque and writes depth is a solid object; a transparent overlay
   * (selection ring, health bar, ground shadow blob, tracer, sprite) is not,
   * and must never cast.
   */
  _tagShadowCasters() {
    const kids = this.scene.children;
    // Cheap trigger for the common case (a layer added a mesh), plus a slow
    // sweep so geometry nested inside a Group -- which does not change
    // scene.children.length -- is picked up within half a second.
    if (kids.length === this._shadowTagged && --this._shadowTagCountdown > 0) return;
    this._shadowTagged = kids.length;
    this._shadowTagCountdown = 30;
    this.scene.traverse(this._tagOne);
  }

  _buildPlacementGhost() {
    // A real footprint projection: one pad per tile, green where that tile can
    // host the structure and red where it cannot, so a blocked placement tells
    // you WHICH tile is the problem instead of just refusing.
    const pad = new THREE.PlaneGeometry(0.9, 0.9);
    pad.rotateX(-Math.PI / 2);
    const padMat = new THREE.MeshBasicMaterial({
      transparent: true, opacity: 0.42, depthWrite: false, fog: false,
      polygonOffset: true, polygonOffsetFactor: -4, polygonOffsetUnits: -4,
    });
    this.ghostPads = new THREE.InstancedMesh(pad, padMat, GHOST_PAD_CAP);
    this.ghostPads.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.ghostPads.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(GHOST_PAD_CAP * 3).fill(1), 3);
    this.ghostPads.instanceColor.setUsage(THREE.DynamicDrawUsage);
    this.ghostPads.count = 0;
    this.ghostPads.frustumCulled = false;
    this.ghostPads.renderOrder = 6;
    this.ghostPads.visible = false;
    this.scene.add(this.ghostPads);

    // A frame around the whole footprint, so the snapped extent is unambiguous.
    const shape = new THREE.Shape();
    shape.moveTo(-0.5, -0.5); shape.lineTo(0.5, -0.5); shape.lineTo(0.5, 0.5); shape.lineTo(-0.5, 0.5); shape.closePath();
    const hole = new THREE.Path();
    hole.moveTo(-0.44, -0.44); hole.lineTo(-0.44, 0.44); hole.lineTo(0.44, 0.44); hole.lineTo(0.44, -0.44); hole.closePath();
    shape.holes.push(hole);
    const frameGeo = new THREE.ShapeGeometry(shape);
    frameGeo.rotateX(Math.PI / 2);
    this.ghostFrame = new THREE.Mesh(frameGeo, new THREE.MeshBasicMaterial({
      transparent: true, opacity: 0.95, depthWrite: false, fog: false, side: THREE.DoubleSide,
      polygonOffset: true, polygonOffsetFactor: -6, polygonOffsetUnits: -6,
    }));
    this.ghostFrame.frustumCulled = false;
    this.ghostFrame.renderOrder = 7;
    this.ghostFrame.visible = false;
    this.scene.add(this.ghostFrame);

    this._ghostOk = uiColor(0x3ff06a);
    this._ghostBad = uiColor(0xff3b28);
    this._ghostM = new THREE.Matrix4();
    this._ghostP = new THREE.Vector3();
    this._ghostQ = new THREE.Quaternion();
    this._ghostS = new THREE.Vector3(1, 1, 1);
  }

  _buildComposer() {
    if (!this.postEnabled) return;
    try {
      this.three.getSize(_size);
      const dpr = this.three.getPixelRatio();
      const w = Math.max(2, Math.round(_size.x * dpr));
      const h = Math.max(2, Math.round(_size.y * dpr));
      // At >=1.75x device pixels the downsample IS the antialiasing, so MSAA
      // storage would be paying twice for the same edges.
      let samples = dpr >= 1.75 ? 0 : 4;
      // A context that advertises WEBGL_multisampled_render_to_texture without
      // implementing its entry point will hard-crash three the first time a
      // multisampled target is bound. Asking for 0 samples is the only way to
      // stay off that path once three has cached the extension.
      if (samples > 0) {
        const gl = this.three.getContext && this.three.getContext();
        const ext = gl && gl.getExtension && gl.getExtension('WEBGL_multisampled_render_to_texture');
        if (ext && typeof ext.framebufferTexture2DMultisampleEXT !== 'function') samples = 0;
      }
      const rt = new THREE.WebGLRenderTarget(w, h, {
        type: THREE.HalfFloatType,
        colorSpace: THREE.LinearSRGBColorSpace,
        depthBuffer: true,
        stencilBuffer: false,
        samples,
      });
      const composer = new EffectComposer(this.three, rt);
      composer.addPass(new RenderPass(this.scene, this.camera.cam));
      const bloom = new UnrealBloomPass(new THREE.Vector2(w, h), BLOOM_STRENGTH, BLOOM_RADIUS, BLOOM_THRESHOLD);
      composer.addPass(bloom);
      composer.addPass(new OutputPass());
      composer.setSize(w, h);
      this.composer = composer;
      this.bloom = bloom;
      this._composerSamples = samples;
      this.stats.postfx = true;
    } catch (err) {
      this.composer = null;
      this.bloom = null;
      this.postEnabled = false;
      this.stats.postfx = false;
      this._postError = err && err.message ? err.message : String(err);
    }
  }

  _disposeComposer() {
    if (!this.composer) return;
    try {
      if (this.composer.renderTarget1) this.composer.renderTarget1.dispose();
      if (this.composer.renderTarget2) this.composer.renderTarget2.dispose();
      for (const p of this.composer.passes) if (p.dispose) p.dispose();
    } catch { /* teardown must never throw */ }
    this.composer = null;
    this.bloom = null;
  }

  /** Bloom on/off at runtime, so the cost is one keypress to measure. */
  setPostFX(on) {
    const want = !!on;
    if (want === !!this.composer) { this.postEnabled = want; return; }
    this.postEnabled = want;
    if (want) this._buildComposer();
    else { this._disposeComposer(); this.stats.postfx = false; }
  }

  /**
   * Frame-time readout. The renderer owns this, not the HUD: it exists to show
   * the cost of what the renderer does, and it has to survive with the HUD
   * turned off. F3 toggles it, F4 toggles bloom so the two can be compared.
   */
  _buildOverlay() {
    if (typeof document === 'undefined' || !document.body) return;
    const d = document.createElement('div');
    d.id = 'rts-perf';
    Object.assign(d.style, {
      position: 'fixed', top: '6px', left: '6px', zIndex: '9999',
      font: '11px/1.45 ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
      color: '#cfe6ff', background: 'rgba(6,10,16,0.62)', padding: '5px 8px',
      borderRadius: '4px', border: '1px solid rgba(120,170,220,0.22)',
      pointerEvents: 'none', whiteSpace: 'pre', textShadow: '0 1px 2px rgba(0,0,0,0.8)',
    });
    document.body.appendChild(d);
    this.perfEl = d;
    this.perfVisible = true;

    // Vignette. A CSS radial gradient over the canvas costs zero GPU time in
    // our budget and cannot introduce a shader bug; it sits under #hud in DOM
    // order so it never dims the HUD itself.
    const v = document.createElement('div');
    v.id = 'rts-vignette';
    Object.assign(v.style, {
      position: 'fixed', inset: '0', zIndex: '1', pointerEvents: 'none',
      background: 'radial-gradient(ellipse 78% 74% at 50% 46%, rgba(0,0,0,0) 42%, rgba(0,0,0,0.30) 78%, rgba(4,7,12,0.62) 100%)',
    });
    const app = document.getElementById('app') || document.body;
    const hudEl = document.getElementById('hud');
    if (hudEl && hudEl.parentNode === app && app.insertBefore) app.insertBefore(v, hudEl);
    else app.appendChild(v);
    this.vignetteEl = v;

    this._onKey = (e) => {
      if (e.key === 'F3') { this.perfVisible = !this.perfVisible; d.style.display = this.perfVisible ? 'block' : 'none'; if (e.preventDefault) e.preventDefault(); }
      else if (e.key === 'F4') { this.setPostFX(!this.composer); if (e.preventDefault) e.preventDefault(); }
    };
    if (typeof addEventListener === 'function') addEventListener('keydown', this._onKey);
    this._updateOverlay();
  }

  // ---- contract surface --------------------------------------------------
  resize() {
    const cv = this.canvas;
    let w = cv.clientWidth | 0, h = cv.clientHeight | 0;
    if (!w || !h) {
      // The stylesheet has not sized us yet; fall back to the viewport so the
      // very first frame is not a 300x150 default canvas.
      w = (typeof window !== 'undefined' ? window.innerWidth : 1280) | 0;
      h = (typeof window !== 'undefined' ? window.innerHeight : 720) | 0;
      cv.style.display = 'block';
      cv.style.width = '100%';
      cv.style.height = '100%';
    }
    const dpr = Math.min(2, (typeof window !== 'undefined' && window.devicePixelRatio) || 1);
    this.three.setPixelRatio(dpr);
    this.three.setSize(w, h, false);
    this.camera.setViewport(w, h);

    if (this.composer) {
      const want = dpr >= 1.75 ? 0 : 4;
      if (want !== this._composerSamples) { this._disposeComposer(); this._buildComposer(); }
      else this.composer.setSize(Math.round(w * dpr), Math.round(h * dpr));
    }
  }

  screenToWorld(sx, sy) { return this.camera.screenToWorld(sx, sy); }

  /**
   * Tile coordinates -> canvas pixels, with `z` measured ABOVE THE TERRAIN
   * SURFACE rather than above absolute zero.
   *
   * This matters, and it is the difference between the game feeling accurate and
   * feeling broken. screenToWorld() picks against the real terrain, so it always
   * answers with a point on the surface. If worldToScreen(x, y, 0) meant "sea
   * level" instead of "the ground at x,y", then on player 0's plateau the two
   * would disagree by TERRAIN_HIGH / tan(55 deg) = 1.19 tiles -- more than a
   * unit's diameter. Every caller that projects a world point, hit-tests it and
   * clicks it (drag-select, unit labels, the synthetic player) would be off by a
   * unit at the exact spot where the whole game is played.
   *
   * Pass FLY_ALT for a flyer, or use entityScreenPos() which handles it.
   */
  worldToScreen(x, y, z = 0) { return this.camera.surfaceToScreen(x, y, z); }

  /**
   * Screen position of an entity's VISUAL centre -- interpolated the same way
   * render() draws it, and lifted to flight altitude for flyers, so UI anchored
   * here sits on the sprite and not on the ground under it.
   */
  entityScreenPos(e, alpha = 1) {
    if (!e) return null;
    let x = e.x, y = e.y;
    if (e.kind === 'unit' && e.px !== undefined) {
      x = e.px + (e.x - e.px) * alpha;
      y = e.py + (e.y - e.py) * alpha;
    }
    const lift = e.flying ? FLY_ALT : (e.kind === 'building' ? 0.9 : 0.45);
    return this.camera.surfaceToScreen(x, y, lift);
  }

  /**
   * Order-acknowledgement marker: a ring on the ground where a command landed.
   * The input layer calls this on right-click so the player gets confirmation on
   * the very frame of the click, not on the next simulation tick.
   */
  ping(x, y, kind = 'move') {
    const c = kind === 'attack' ? [1.0, 0.32, 0.24]
      : kind === 'rally' ? [1.0, 0.85, 0.35]
        : [0.32, 1.0, 0.42];
    this.fx.addRing(x, this.sampleHeight(x, y) + 0.08, y, 0.25, 1.35, 0.42, c[0], c[1], c[2]);
  }

  setSelection(ids) {
    this.selection.clear();
    if (ids) for (const id of ids) this.selection.add(id);
    this.entities.setSelection(this.selection);
  }

  /**
   * Show or clear the building placement ghost. Snapping matches
   * commands.validatePlacement exactly, so what the ghost shows is what the
   * build command will actually do.
   */
  setPlacement(defId, x, y, valid) {
    if (!defId || !BuildingDefs[defId]) {
      this.placement = null;
      this.ghostPads.visible = false;
      this.ghostFrame.visible = false;
      if (this.entities.setGhost) this.entities.setGhost(null);
      return;
    }
    const def = BuildingDefs[defId];
    const spot = validatePlacement(this.world, this.player, defId, x, y);
    let tx, ty;
    if (spot.ok) { tx = spot.tx; ty = spot.ty; }
    else { tx = Math.round(x - def.size / 2); ty = Math.round(y - def.size / 2); }
    const size = def.size;
    const cx = tx + size / 2, cy = ty + size / 2;
    const ok = valid === undefined || valid === null ? spot.ok : !!valid;

    this.placement = { defId, x: cx, y: cy, tx, ty, size, valid: ok };
    this._updateGhostPads(tx, ty, size, ok, !!def.onGeyser && spot.ok);
    if (this.entities.setGhost) this.entities.setGhost(defId, cx, cy, size, ok);
  }

  /** Per-tile footprint projection, snapped to the grid, on the real surface. */
  _updateGhostPads(tx, ty, size, ok, allValid) {
    const g = this.world.grid;
    const pads = this.ghostPads;
    const arr = pads.instanceMatrix.array;
    const cols = pads.instanceColor.array;
    const lvl = g.inBounds(tx, ty) ? g.height[ty * g.w + tx] : 0;
    let k = 0;
    for (let j = ty; j < ty + size; j++) {
      for (let i = tx; i < tx + size; i++) {
        if (k >= GHOST_PAD_CAP) break;
        const good = allValid || tileBuildable(g, i, j, lvl);
        const c = good ? this._ghostOk : this._ghostBad;
        this._ghostP.set(i + 0.5, this.sampleHeight(i + 0.5, j + 0.5) + 0.045, j + 0.5);
        this._ghostM.compose(this._ghostP, this._ghostQ, this._ghostS);
        this._ghostM.toArray(arr, k * 16);
        cols[k * 3] = c[0]; cols[k * 3 + 1] = c[1]; cols[k * 3 + 2] = c[2];
        k++;
      }
    }
    pads.count = k;
    pads.instanceMatrix.needsUpdate = true;
    pads.instanceColor.needsUpdate = true;
    pads.visible = k > 0;

    const c = ok ? this._ghostOk : this._ghostBad;
    this.ghostFrame.material.color.setRGB(c[0], c[1], c[2], THREE.LinearSRGBColorSpace);
    this.ghostFrame.position.set(tx + size / 2, this.sampleHeight(tx + size / 2, ty + size / 2) + 0.06, ty + size / 2);
    this.ghostFrame.scale.set(size, 1, size);
    this.ghostFrame.visible = true;
  }

  /** Subscribe to the per-frame simulation event batch. cb(events, world). */
  onEvents(cb) { if (typeof cb === 'function') this._eventCbs.push(cb); return () => {
    const i = this._eventCbs.indexOf(cb);
    if (i >= 0) this._eventCbs.splice(i, 1);
  }; }

  /** The events drained this frame. Valid until the next render() call. */
  getFrameEvents() { return this.frameEvents; }

  // ---- frame -------------------------------------------------------------
  render(alpha) {
    const now = (typeof performance !== 'undefined' ? performance.now() : Date.now());
    let dt = (now - this._last) / 1000;
    this._last = now;
    if (!(dt > 0)) dt = 0;
    if (dt > 0.1) dt = 0.1;

    // If nobody called camera.update() this frame, drive it ourselves so edge
    // scroll / keys / wheel keep working regardless of the input layer.
    this.camera.tick(dt);

    // ONE drain per frame; hand the batch to everyone who wants it.
    const evs = this.world.drainEvents();
    this.frameEvents = evs;
    if (evs.length) {
      for (let i = 0; i < this._eventCbs.length; i++) this._eventCbs[i](evs, this.world);
      this.fx.consume(evs);
    }

    this._updateFog();

    const cam = this.camera.cam;
    this._camUp.set(0, 1, 0).applyQuaternion(cam.quaternion);
    const a = alpha === undefined || alpha === null ? 1 : Math.max(0, Math.min(1, alpha));
    this.entities.update(a, cam.quaternion, this._camUp);
    this.fx.update(dt, cam.quaternion);
    this._tagShadowCasters();

    // Sky rides with the camera so the horizon never runs out.
    this.sky.position.copy(cam.position);
    this.sky.updateMatrix();

    // Distance fog scaled to zoom: the played area stays crisp, the map edge
    // dissolves into the same colour the horizon is painted.
    const f = this.scene.fog;
    f.near = this.camera.dist * 1.35;
    f.far = this.camera.dist * 3.2;

    this._fitShadow();

    this.three.info.reset();
    if (this.composer) this.composer.render(dt);
    else this.three.render(this.scene, cam);

    const ms = (typeof performance !== 'undefined' ? performance.now() : Date.now()) - now;
    this.stats.frameMs = ms;
    this.stats.cpuMs = ms;
    this.stats.drawCalls = this.three.info.render.calls;
    this.stats.triangles = this.three.info.render.triangles;
    this.stats.units = this.entities.stats.units;
    this.stats.buildings = this.entities.stats.buildings;
    this.stats.particles = this.fx.stats.particles;
    this.stats.postfx = !!this.composer;
    this._fpsAcc += dt; this._fpsN++; this._msAcc += ms;
    if (this._fpsAcc >= 0.5) {
      this.stats.fps = this._fpsN / this._fpsAcc;
      this.stats.cpuMs = this._msAcc / this._fpsN;
      this._fpsAcc = 0; this._fpsN = 0; this._msAcc = 0;
      this._updateOverlay();
    }
  }

  /**
   * Fit the sun's orthographic shadow camera to what is actually on screen.
   *
   * A shadow camera covering the whole 96x96 map would spend 2048 texels on
   * 96 tiles -- 21 texels per tile, so a rifleman would cast about four texels
   * of shadow. Fitting it to camera.viewRect() instead puts the same 2048
   * texels across ~30 tiles at default zoom, which is roughly 70 per tile.
   *
   * Two anti-shimmer measures, both mandatory:
   *   * the radius is QUANTISED, so the texel size only changes in steps -- if
   *     it changed continuously nothing below could stabilise it;
   *   * the centre is SNAPPED to whole texels in the light's own basis, which
   *     is what stops shadow edges crawling as the camera pans.
   */
  _fitShadow() {
    const cam = this.camera;
    const r = cam.viewRectInto ? cam.viewRectInto(_rectScratch) : cam.viewRect();
    const fit = fitSunShadow(_shadowFit, r, this.terrainMinZ, this.terrainMaxZ);
    const back = fit.halfW + 45;

    const sun = this.sun;
    sun.target.position.set(fit.x, fit.y, fit.z);
    sun.target.updateMatrixWorld();
    sun.position.copy(sun.target.position).addScaledVector(SUN_DIR, back);
    sun.updateMatrixWorld();

    const sc = sun.shadow.camera;
    if (sc.right !== fit.halfU || sc.top !== fit.halfV) {
      sc.left = -fit.halfU; sc.right = fit.halfU;
      sc.top = fit.halfV; sc.bottom = -fit.halfV;
      sc.near = 0.5; sc.far = back + fit.halfW + 45;
      sc.updateProjectionMatrix();
    }
  }

  _updateOverlay() {
    const el = this.perfEl;
    if (!el || !this.perfVisible) return;
    const s = this.stats;
    el.textContent =
      `${s.fps.toFixed(0)} fps   ${s.cpuMs.toFixed(2)} ms cpu\n`
      + `draws ${s.drawCalls}   tris ${(s.triangles / 1000).toFixed(0)}k\n`
      + `units ${s.units}  bld ${s.buildings}  fx ${s.particles}\n`
      + `bloom ${s.postfx ? 'on' : 'off'}   F3 hide  F4 bloom`;
  }

  _updateFog() {
    const f = this.world.fog[this.player];
    const v = f.version === undefined ? this.world.tick : f.version;
    if (v === this._fogVersion) return;
    this._fogVersion = v;
    const src = f.vis, dst = this._fogData;
    for (let i = 0; i < src.length; i++) dst[i] = (FOG_LUT[src[i]] * 255) | 0;
    this.fogTex.needsUpdate = true;
  }

  dispose() {
    if (this._onKey && typeof removeEventListener === 'function') removeEventListener('keydown', this._onKey);
    if (this.perfEl && this.perfEl.remove) this.perfEl.remove();
    if (this.vignetteEl && this.vignetteEl.remove) this.vignetteEl.remove();
    this._disposeComposer();
    this.camera.detach();
    this.entities.dispose();
    this.fx.dispose();
    this.terrain.geometry.dispose();
    this.terrainMat.dispose();
    this.sky.geometry.dispose();
    this.sky.material.dispose();
    this.ghostPads.geometry.dispose();
    this.ghostPads.material.dispose();
    this.ghostFrame.geometry.dispose();
    this.ghostFrame.material.dispose();
    this.fogTex.dispose();
    this.surfaceTex.dispose();
    this.three.dispose();
  }
}

const _shadowFit = { halfU: 0, halfV: 0, halfW: 0, texelU: 0, texelV: 0, x: 0, y: 0, z: 0 };

/**
 * Where to point the sun's orthographic shadow camera, given the world-tile
 * rect currently on screen and the elevation band the terrain occupies.
 *
 * This is pulled out of the Renderer because it is the whole anti-shimmer
 * story and it is pure maths -- no WebGL, so it can be tested. Two rules, and
 * BOTH are load-bearing:
 *
 *   * QUANTISE THE RADIUS. Texel size is 2 * radius / SHADOW_SIZE. If the
 *     radius tracked the view rect continuously, every frame would resample the
 *     scene onto a slightly different grid and every shadow edge would boil.
 *     Rounding up to SHADOW_STEP, with hysteresis on the way back down, means
 *     texel size is constant through an entire pan.
 *   * SNAP THE CENTRE TO WHOLE TEXELS, in the light's own basis rather than in
 *     world axes -- the shadow grid is aligned to the light, not to the map.
 *     With a constant texel size this pins the sample grid in place, so panning
 *     slides the scene across a fixed grid instead of dragging the grid along
 *     with it. That is the difference between crisp shadows and crawling ones.
 *
 * `prevRadius` is last frame's radius; pass 0 on the first call.
 */
export function fitSunShadow(out, rect, minY, maxY, dir = SUN_DIR, mapSize = SHADOW_SIZE) {
  _sunRight.crossVectors(_worldUp, dir).normalize();
  _sunUp.crossVectors(dir, _sunRight).normalize();

  // The camera's yaw is fixed, so a light-space AABB of the view volume is
  // stable in shape and far tighter than a bounding sphere would be: the view
  // rect is roughly twice as wide as it is deep, and a sphere around it would
  // throw away most of the shadow map on empty sky.
  const yLo = minY - 0.5;
  const yHi = maxY + FLY_ALT + 1.5;
  let uMin = Infinity, uMax = -Infinity, vMin = Infinity, vMax = -Infinity, wMin = Infinity, wMax = -Infinity;
  for (let c = 0; c < 8; c++) {
    const x = (c & 1) ? rect.x1 : rect.x0;
    const y = (c & 2) ? yHi : yLo;
    const z = (c & 4) ? rect.y1 : rect.y0;
    const u = x * _sunRight.x + y * _sunRight.y + z * _sunRight.z;
    const v = x * _sunUp.x + y * _sunUp.y + z * _sunUp.z;
    const w = x * dir.x + y * dir.y + z * dir.z;
    if (u < uMin) uMin = u; if (u > uMax) uMax = u;
    if (v < vMin) vMin = v; if (v > vMax) vMax = v;
    if (w < wMin) wMin = w; if (w > wMax) wMax = w;
  }

  const quantise = (half, prev) => {
    let q = Math.ceil((half + SHADOW_PAD) / SHADOW_STEP) * SHADOW_STEP;
    // Grow immediately, shrink only after a clear step of slack, so a camera
    // hovering on a bucket boundary cannot flip texel size every other frame.
    if (q < prev && q > prev - SHADOW_STEP * 1.5) q = prev;
    return q;
  };
  const halfU = quantise((uMax - uMin) * 0.5, out.halfU || 0);
  const halfV = quantise((vMax - vMin) * 0.5, out.halfV || 0);

  const texelU = (halfU * 2) / mapSize;
  const texelV = (halfV * 2) / mapSize;
  const cu = Math.round(((uMin + uMax) * 0.5) / texelU) * texelU;
  const cv = Math.round(((vMin + vMax) * 0.5) / texelV) * texelV;
  const cw = (wMin + wMax) * 0.5;

  _sunCentre.set(0, 0, 0)
    .addScaledVector(_sunRight, cu)
    .addScaledVector(_sunUp, cv)
    .addScaledVector(dir, cw);

  out.halfU = halfU;
  out.halfV = halfV;
  out.halfW = (wMax - wMin) * 0.5;
  out.texelU = texelU;
  out.texelV = texelV;
  out.x = _sunCentre.x; out.y = _sunCentre.y; out.z = _sunCentre.z;
  return out;
}

/** Can a structure anchored at level `lvl` occupy this one tile? */
export function tileBuildable(g, x, y, lvl) {
  if (x < 0 || y < 0 || x >= g.w || y >= g.h) return false;
  const k = y * g.w + x;
  if (g.cliff[k]) return false;
  if (g.occ[k] !== -1) return false;
  if (g.ramp[k]) return false;
  if (g.noBuild[k]) return false;
  if (g.height[k] !== lvl) return false;
  return true;
}

// ---------------------------------------------------------------------------
// Sky gradient. Authored as display sRGB and pre-compensated through the
// inverse tone curve, so the horizon really is the colour written here.
// ---------------------------------------------------------------------------
const SKY_STOPS = [
  [-1.00, uiColor(0x1b232c)],   // below the horizon: dark haze
  [-0.06, uiColor(0x515f6d)],
  [0.00, uiColor(0x8d99a5)],   // the horizon line itself
  [0.09, uiColor(0x6f849f)],
  [0.30, uiColor(0x3f5f8c)],
  [0.62, uiColor(0x25406e)],
  [1.00, uiColor(0x152443)],   // zenith
];
export const HORIZON = SKY_STOPS[2][1];

function skyGradient(t) {
  for (let i = 1; i < SKY_STOPS.length; i++) {
    if (t <= SKY_STOPS[i][0] || i === SKY_STOPS.length - 1) {
      const a = SKY_STOPS[i - 1], b = SKY_STOPS[i];
      const span = b[0] - a[0];
      const k = span <= 0 ? 0 : Math.min(1, Math.max(0, (t - a[0]) / span));
      return [
        a[1][0] + (b[1][0] - a[1][0]) * k,
        a[1][1] + (b[1][1] - a[1][1]) * k,
        a[1][2] + (b[1][2] - a[1][2]) * k,
      ];
    }
  }
  return SKY_STOPS[SKY_STOPS.length - 1][1];
}

// ---------------------------------------------------------------------------
// Terrain material: a stock MeshStandardMaterial (so it gets the lighting rig,
// shadow receiving, distance fog and tone mapping for free) with exactly one
// injected idea -- the fog of war, applied AFTER distance fog so an unexplored
// map edge stays black instead of dissolving into the horizon.
// ---------------------------------------------------------------------------
export function makeTerrainMaterial(fogTex, surfaceTex) {
  const mat = new THREE.MeshStandardMaterial({
    vertexColors: true,
    roughness: 1.0,
    metalness: 0.0,
    roughnessMap: surfaceTex || null,
    side: THREE.FrontSide,
    dithering: true,
  });
  mat.onBeforeCompile = (shader) => {
    shader.uniforms.uFow = { value: fogTex };
    shader.vertexShader = 'varying vec2 vFowUv;\n' + shader.vertexShader.replace(
      '#include <begin_vertex>',
      '#include <begin_vertex>\n\tvFowUv = uv;',
    );
    shader.fragmentShader = 'uniform sampler2D uFow;\nvarying vec2 vFowUv;\n' + shader.fragmentShader.replace(
      '#include <fog_fragment>',
      `#include <fog_fragment>
	float fowV = texture2D( uFow, vFowUv ).r;
	float fowLum = clamp( fowV * 2.5, 0.0, 1.0 );
	vec3 fowTint = mix( vec3( 0.26, 0.30, 0.40 ), vec3( 1.0 ), clamp( ( fowV - 0.4 ) * 1.6667, 0.0, 1.0 ) );
	gl_FragColor.rgb *= fowTint * fowLum;`,
    );
    mat.userData.shader = shader;
  };
  // Distinct cache key so this variant never shares a program with a plain
  // MeshStandardMaterial that happens to have the same defines.
  mat.customProgramCacheKey = () => 'terrain-fow-v1';
  return mat;
}

export { PALETTE, TERRAIN_HIGH, FLY_ALT, SUN_DIR };
